/**
 * navItems.ts — Single source of truth for navigation items.
 *
 * Import this into: AppSidebar, SearchPopup, CommandMenu.
 * Changing an entry here updates ALL three places simultaneously.
 *
 * Icons are Hugeicons data objects — ALWAYS render with:
 *   <HugeiconsIcon icon={item.icon} className="..." />
 * NEVER as <item.icon /> — that causes React error #130 ("got: object").
 */
import {
  Home05Icon,
  ChartIcon,
  Folder03Icon,
  TableIcon,
  ConnectIcon,
  WebProgrammingIcon,
  SourceCodeIcon,
  Settings03Icon,
  Mail01Icon,
  CreditCardIcon as CreditCard02Icon,
  UserMultipleIcon,
  Notification01Icon,
} from '@hugeicons/core-free-icons';

export interface NavItem {
  id: string;
  title: string;
  url: string;
  /** Hugeicons data object — render with <HugeiconsIcon icon={item.icon} /> */
  icon: any;
  group: 'main' | 'settings';
  description?: string;
}

/** Main navigation — mirrors AppSidebar mainItems exactly */
export const mainNavItems: NavItem[] = [
  { id: 'home',      title: 'Home',      url: '/',          icon: Home05Icon,          group: 'main' },
  { id: 'analytics', title: 'Analytics', url: '/analytics', icon: ChartIcon,           group: 'main' },
  { id: 'storage',   title: 'Storage',   url: '/files',     icon: Folder03Icon,        group: 'main', description: 'Files & buckets' },
  { id: 'database',  title: 'Database',  url: '/database',  icon: TableIcon,           group: 'main', description: 'NoSQL documents' },
  { id: 'hosting',   title: 'Hosting',   url: '/hosting',   icon: ConnectIcon,         group: 'main', description: 'Deployed apps' },
  { id: 'pages',     title: 'Pages',     url: '/pages',     icon: WebProgrammingIcon,  group: 'main', description: 'Static sites' },
  { id: 'functions', title: 'Functions', url: '/functions', icon: SourceCodeIcon,      group: 'main', description: 'Serverless endpoints' },
  { id: 'email',     title: 'Email',     url: '/email',     icon: Mail01Icon,          group: 'main', description: 'Inboxes & sending' },
];

/** Settings shortcuts for command palette / search */
export const settingsNavItems: NavItem[] = [
  { id: 'settings',              title: 'Settings',          url: '/settings',                       icon: Settings03Icon,      group: 'settings' },
  { id: 'settings-billing',      title: 'Billing & Credits', url: '/settings?tab=billing',           icon: CreditCard02Icon,    group: 'settings' },
  { id: 'settings-team',         title: 'Team Members',      url: '/settings?tab=members',           icon: UserMultipleIcon,    group: 'settings' },
  { id: 'settings-notifications',title: 'Notifications',     url: '/settings?tab=notifications',     icon: Notification01Icon,  group: 'settings' },
  { id: 'settings-integrations', title: 'GitHub & GitLab',   url: '/settings?tab=integrations',      icon: ConnectIcon,         group: 'settings' },
];

/** All items flat — convenience for search/filter */
export const allNavItems: NavItem[] = [...mainNavItems, ...settingsNavItems];
